import React from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';

// Receipt strip shown in the workspace footer after Update Route confirms.
// Status flow: Submitted → Processing → Confirmed (or Failed).
// Owns no fetch logic — the parent owns polling and updates `status` props.

export const RECEIPT_STATUS = {
  SUBMITTED: 'submitted',
  PROCESSING: 'processing',
  CONFIRMED: 'confirmed',
  FAILED: 'failed',
};

const STATUS_LABELS = {
  [RECEIPT_STATUS.SUBMITTED]: 'Reroute submitted',
  [RECEIPT_STATUS.PROCESSING]: 'Processing…',
  [RECEIPT_STATUS.CONFIRMED]: 'Reroute confirmed ✓',
  [RECEIPT_STATUS.FAILED]: 'Reroute failed — rolling back',
};

const StyledDiv = styledComponent('div', {
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing.fieldGap,
  padding: theme.spacing.bodyPadding,
  background: theme.colors.bgCard,
  border: theme.borders.card,
  borderRadius: theme.radii.card,
  marginBottom: theme.spacing.sectionGap,

  '&.is-confirmed': {
    borderColor: theme.colors.primary,
    background: theme.colors.successBg,
  },
  '&.is-failed': {
    borderColor: theme.colors.warning,
    background: theme.colors.warningBg,
  },

  '& .nrs-label': {
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textDark,
  },
  '& .nrs-meta': {
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textMuted,
    flex: 1,
  },
  '& .nrs-dismiss': {
    background: 'none',
    border: 'none',
    color: theme.colors.textMuted,
    cursor: 'pointer',
    fontSize: theme.typography.body.fontSize,
    padding: '4px 8px',
  },
});

const STATE_CLASS = {
  [RECEIPT_STATUS.CONFIRMED]: 'is-confirmed',
  [RECEIPT_STATUS.FAILED]: 'is-failed',
};

function NextmvReceiptStrip({
  status,
  routeName,
  runId,
  errorMessage,
  onDismiss,
}) {
  const stateClass = STATE_CLASS[status] || '';

  return (
    <StyledDiv className={stateClass} data-testid="nextmv-receipt-strip">
      <span className="nrs-label">{STATUS_LABELS[status]}</span>
      <span className="nrs-meta">
        {routeName ? `Route: ${routeName}` : ''}
        {runId ? ` · Run ${runId}` : ''}
        {status === RECEIPT_STATUS.FAILED && errorMessage
          ? ` · ${errorMessage}`
          : ''}
      </span>
      {(status === RECEIPT_STATUS.CONFIRMED ||
        status === RECEIPT_STATUS.FAILED) && (
        <button
          type="button"
          className="nrs-dismiss"
          onClick={onDismiss}
          data-testid="nextmv-receipt-dismiss"
        >
          Dismiss
        </button>
      )}
    </StyledDiv>
  );
}

NextmvReceiptStrip.propTypes = {
  status: PropTypes.oneOf(Object.values(RECEIPT_STATUS)).isRequired,
  routeName: PropTypes.string,
  runId: PropTypes.string,
  errorMessage: PropTypes.string,
  onDismiss: PropTypes.func,
};

NextmvReceiptStrip.defaultProps = {
  routeName: '',
  runId: '',
  errorMessage: '',
  onDismiss: () => {},
};

export default NextmvReceiptStrip;
